import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first } from 'rxjs';
import { SessionService } from 'src/app/services/session.service';
import { BaseWizardComponent } from '../base-wizard.component';
import { ActivityStorageDataModel, ExchangeAreaDataModel, IActivityStorageDataModel, IExchangeAreaDataModel, ILotPlanModel, IPurposeDataModel, LotPlanModel, PurposeDataModel } from '../models/veg-wizard-models';
import { ExchangeAreaService } from '../services/exchange-area.service';
import { UtilService } from '../services/util.service';
import { VegWizardDataService } from '../services/veg-wizard-data.service';

@UntilDestroy()
@Component({
  selector: 'app-exchange-area',
  templateUrl: './exchange-area.component.html',
  styleUrls: ['./exchange-area.component.css'],
  imports: [
    CommonModule,   
    ReactiveFormsModule,
  ],
})
export class ExchangeAreaComponent extends BaseWizardComponent implements OnInit {
  form: FormGroup;
  sessionId = '';

  get exchangeAreaReferenceNumberControl() { return this.form.controls['exchangeAreaReferenceNumber']; }
  get isExchangeAreaRequiredControl() { return this.form.controls['isExchangeAreaRequired']; }

  exchangeAreaData: IExchangeAreaDataModel;
  lotOnPlan: ILotPlanModel | undefined;
  purpose: IPurposeDataModel | undefined;
  activity: IActivityStorageDataModel | undefined;
  nextButtonClicked = false;

  REGEX_PATTERN_REFERENCENUMBER = /^(20)\d{2}\/\d{6}$/;

  exchangeAreaErrorMessage: string | null = null;
  exchangeAreaQuestions: string[] = [];
  exchangeAreaTitle = '';
  displayKoalaHabitatProtectionInfoMessage = false;
  isExchangeAreaReferenceInputValid = false;
  formIsValid = false;

  constructor(
    private formBuilder: FormBuilder,
    private vwds: VegWizardDataService,
    private sessionService: SessionService,
    private exchangeAreaService: ExchangeAreaService,
    private utilService: UtilService,
    router: Router
  ) {
    super();

    this.activity = this.vwds.getActivities();
    this.purpose = this.vwds.getPurpose();
    this.lotOnPlan = this.vwds.getLotPlanModel();

    if (!this.purpose || !this.lotOnPlan || (this.vwds.getCategories()?.some(category => category.requiresActivities) && !this.activity)) {
      throw new Error('Activity, purpose or lot plan have not been completed.');
    }

    this.sessionId = this.sessionService.getSessionIdOrError();
    this.exchangeAreaData = this.vwds.getExchangeArea() ?? new ExchangeAreaDataModel();

    this.form = this.formBuilder.group({
      exchangeAreaReferenceNumber: [this.exchangeAreaData?.exchangeAreaFileReferenceNumber],
      isExchangeAreaRequired: [this.exchangeAreaData?.userOverrideIsExchangeAreaRequired, Validators.required],
    });
  }

  ngOnInit(): void {
    if (this.exchangeAreaData.userOverrideIsExchangeAreaRequired === false)
      this.exchangeAreaData.exchangeAreaFileReferenceNumber = '';

    const exchangeAreaInformation = this.exchangeAreaService.getExchangeAreaInformation(
      this.activity ?? new ActivityStorageDataModel(),
      this.purpose ?? new PurposeDataModel()
    );

    if (exchangeAreaInformation !== null) {
      this.exchangeAreaQuestions = exchangeAreaInformation.questions;
      this.exchangeAreaTitle = exchangeAreaInformation.header;
    }

    this.displayKoalaHabitatProtectionInfoMessage = this.exchangeAreaService.determineToShowKoalaHabitatProtectionMessage(
      this.lotOnPlan ?? new LotPlanModel(),
      this.activity ?? new ActivityStorageDataModel(),
      this.purpose ?? new PurposeDataModel()
    );

    this.performValidation();

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.performValidation();
    });

  }

  clearExchangeAreaReferenceNumber() {
    this.exchangeAreaReferenceNumberControl.setValue('');
  }

  exchangeAreaReferenceInputValid(): boolean {

    if (this.nextButtonClicked) {
      // check that value is not null, and allow to pass if user selects false
      if (this.isExchangeAreaRequiredControl.value && this.isExchangeAreaRequiredControl.value == false) {
        return true;
      }
      // if user selects true, it must meet regex requirements
      return this.REGEX_PATTERN_REFERENCENUMBER.test(this.exchangeAreaReferenceNumberControl.value);
    }
    else if (this.exchangeAreaReferenceNumberControl.dirty) {
      return this.REGEX_PATTERN_REFERENCENUMBER.test(this.exchangeAreaReferenceNumberControl.value);
    }
    return true;
  }

  performValidation() {
    this.isExchangeAreaReferenceInputValid = this.exchangeAreaReferenceInputValid();

    this.exchangeAreaReferenceNumberControl?.setErrors(null); // clear form validation errors to begin

    if (!this.isExchangeAreaRequiredControl.value) {
      this.exchangeAreaData.exchangeAreaFileReferenceNumber = '';
    }

    if (!!this.isExchangeAreaRequiredControl.value && this.isExchangeAreaRequiredControl.value && !this.exchangeAreaReferenceInputValid()) {
      this.exchangeAreaReferenceNumberControl?.setErrors({ 'exchangeAreaReferenceNumberControl Required valid': true });
    }

  }

  override next() {
    this.form.markAsDirty();
    this.nextButtonClicked = true;
    this.form.updateValueAndValidity;
    this.performValidation();

    if (this.form.invalid) {
      this.utilService.getFormValidationErrors(this.form);
      return;
    }

    this.exchangeAreaData.userOverrideIsExchangeAreaRequired = this.isExchangeAreaRequiredControl.value;
    this.exchangeAreaData.exchangeAreaFileReferenceNumber = this.exchangeAreaReferenceNumberControl.value;

    this.vwds
      .setExchangeArea$(this.exchangeAreaData)
      .pipe(first(), untilDestroyed(this))
      .subscribe((success) => {
        if (success) {
          super.next();
        }
      });
  }
}
